import { useState } from "react";
import { api, formatApiError } from "@/context/AuthContext";

export default function InstructorCustomCategoriesPanel({ categories, onCategoriesChange }) {
  const [newLabel, setNewLabel] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editLabel, setEditLabel] = useState("");
  const [editDesc, setEditDesc] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const active = (categories || []).filter((c) => c.active && !c.archived);
  const archived = (categories || []).filter((c) => c.archived);

  const refresh = async () => {
    const res = await api.get("/instructor/custom-categories");
    onCategoriesChange?.(res.data.categories || []);
  };

  const createCategory = async (e) => {
    e.preventDefault();
    setMessage("");
    setError("");
    if (!newLabel.trim()) {
      setError("Enter a category label.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/instructor/custom-categories", {
        label: newLabel.trim(),
        description: newDesc.trim(),
      });
      setNewLabel("");
      setNewDesc("");
      setMessage("Category created.");
      await refresh();
    } catch (err) {
      setError(formatApiError(err) || "Could not create category.");
    } finally {
      setBusy(false);
    }
  };

  const startEdit = (cat) => {
    setEditingId(cat.id);
    setEditLabel(cat.label);
    setEditDesc(cat.description || "");
    setMessage("");
    setError("");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditLabel("");
    setEditDesc("");
  };

  const saveEdit = async (categoryId) => {
    setMessage("");
    setError("");
    if (!editLabel.trim()) {
      setError("Label is required.");
      return;
    }
    setBusy(true);
    try {
      await api.put(`/instructor/custom-categories/${categoryId}`, {
        label: editLabel.trim(),
        description: editDesc.trim(),
      });
      setMessage("Category updated.");
      cancelEdit();
      await refresh();
    } catch (err) {
      setError(formatApiError(err) || "Could not update category.");
    } finally {
      setBusy(false);
    }
  };

  const archiveCategory = async (categoryId) => {
    if (!window.confirm("Archive this category? It will no longer appear on new assessments.")) return;
    setMessage("");
    setError("");
    setBusy(true);
    try {
      await api.put(`/instructor/custom-categories/${categoryId}`, { archived: true });
      setMessage("Category archived.");
      if (editingId === categoryId) cancelEdit();
      await refresh();
    } catch (err) {
      setError(formatApiError(err) || "Could not archive category.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="audit-surface audit-section audit-custom-categories" data-testid="instructor-custom-categories">
      <div className="audit-panel-header">
        <div>
          <h2 className="audit-section-title">Your teaching categories</h2>
          <p className="audit-custom-categories-intro">
            Private to you — learners never see these labels, scores, or notes.
          </p>
        </div>
      </div>

      {message && (
        <p role="status" className="text-sm mt-2" data-testid="icc-message">
          {message}
        </p>
      )}
      {error && (
        <p role="alert" className="text-destructive text-sm mt-2" data-testid="icc-error">
          {error}
        </p>
      )}

      <form className="audit-custom-category-form space-y-2" onSubmit={createCategory} data-testid="icc-create-form">
        <label className="block">
          New category
          <input
            type="text"
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            placeholder="e.g. Hill starts, Motorway merge"
            maxLength={80}
            disabled={busy}
            data-testid="icc-new-label"
          />
        </label>
        <label className="block text-sm">
          Description (optional)
          <input
            type="text"
            value={newDesc}
            onChange={(e) => setNewDesc(e.target.value)}
            maxLength={200}
            disabled={busy}
            data-testid="icc-new-desc"
          />
        </label>
        <button type="submit" className="audit-btn-compact" style={{ marginLeft: 0 }} disabled={busy} data-testid="icc-create-btn">
          Add category
        </button>
      </form>

      {active.length > 0 && (
        <ul className="mt-4 space-y-0 list-none p-0 m-0" data-testid="icc-active-list">
          {active.map((cat) => (
            <li key={cat.id} className="audit-custom-category-item">
              {editingId === cat.id ? (
                <div className="space-y-2">
                  <input
                    type="text"
                    className="w-full min-h-11 px-3 py-2 border rounded text-sm"
                    style={{ borderColor: "rgba(22,25,28,.24)" }}
                    value={editLabel}
                    onChange={(e) => setEditLabel(e.target.value)}
                    maxLength={80}
                    data-testid={`icc-edit-label-${cat.id}`}
                  />
                  <input
                    type="text"
                    className="w-full min-h-11 px-3 py-2 border rounded text-sm"
                    style={{ borderColor: "rgba(22,25,28,.24)" }}
                    value={editDesc}
                    onChange={(e) => setEditDesc(e.target.value)}
                    maxLength={200}
                    placeholder="Description"
                    data-testid={`icc-edit-desc-${cat.id}`}
                  />
                  <div className="audit-custom-category-actions">
                    <button
                      type="button"
                      className="audit-btn-compact"
                      style={{ marginLeft: 0 }}
                      onClick={() => saveEdit(cat.id)}
                      disabled={busy}
                      data-testid={`icc-save-${cat.id}`}
                    >
                      Save
                    </button>
                    <button type="button" className="audit-btn-compact" style={{ marginLeft: 0 }} onClick={cancelEdit} disabled={busy}>
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="audit-custom-category-label">{cat.label}</div>
                    {cat.description ? <div className="audit-custom-category-desc">{cat.description}</div> : null}
                  </div>
                  <div className="audit-custom-category-actions">
                    <button
                      type="button"
                      className="audit-btn-compact"
                      style={{ marginLeft: 0 }}
                      onClick={() => startEdit(cat)}
                      disabled={busy}
                      data-testid={`icc-edit-${cat.id}`}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className="audit-btn-compact"
                      style={{ marginLeft: 0 }}
                      onClick={() => archiveCategory(cat.id)}
                      disabled={busy}
                      data-testid={`icc-archive-${cat.id}`}
                    >
                      Archive
                    </button>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {archived.length > 0 && (
        <details className="mt-4 text-sm">
          <summary className="cursor-pointer min-h-11 flex items-center text-muted-foreground">
            Archived ({archived.length})
          </summary>
          <ul className="mt-2 space-y-1 list-disc list-inside" data-testid="icc-archived-list">
            {archived.map((cat) => (
              <li key={cat.id}>{cat.label}</li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}
